import type { ElvaFederatedRemoteModule } from '@elva/ai-chat';
import * as React from 'react';
import * as ReactDOM from 'react-dom';
import * as reactQuery from '@tanstack/react-query';
import * as zustand from 'zustand';

const REMOTE_ENTRY_URL = 'https://chat.elva.ai/assets/remoteEntry.js';

/** chat.elva.ai remote is built against React 19 — host must share the same major. */
const REACT_SHARE_VERSIONS = ['19.0.0', '19.1.0'] as const;

type RemoteContainer = {
  get: (module: string) => Promise<() => Record<string, unknown>>;
  init: (shareScope: Record<string, unknown>) => void;
};

type ShareScopeEntry = {
  get: () => () => unknown;
  loaded: boolean;
  from: string;
};

function buildVersionedShare<T>(lib: T): Record<string, ShareScopeEntry> {
  return Object.fromEntries(
    REACT_SHARE_VERSIONS.map((version) => [
      version,
      {
        get: () => () => lib,
        loaded: true,
        from: 'elva_frontdesk_host',
      },
    ]),
  );
}

function createShareScope() {
  return {
    react: buildVersionedShare(React),
    'react-dom': buildVersionedShare(ReactDOM),
    zustand: buildVersionedShare(zustand),
    '@tanstack/react-query': buildVersionedShare(reactQuery),
  };
}

let remoteContainerPromise: Promise<RemoteContainer> | null = null;

async function loadRemoteContainer(): Promise<RemoteContainer> {
  if (!remoteContainerPromise) {
    remoteContainerPromise = import(
      /* @vite-ignore */
      REMOTE_ENTRY_URL
    ).then((module) => {
      const container = module as RemoteContainer;
      container.init(createShareScope());
      return container;
    });
  }
  return remoteContainerPromise;
}

function normalizeRemoteExports(
  remote: unknown,
): Partial<ElvaFederatedRemoteModule> {
  if (!remote || typeof remote !== 'object') return {};

  const candidate = remote as Record<string, unknown> & {
    default?: Record<string, unknown>;
  };

  if (
    candidate.default &&
    typeof candidate.default === 'object' &&
    !candidate.ElvaFederatedChatProvider
  ) {
    return candidate.default as Partial<ElvaFederatedRemoteModule>;
  }

  return candidate as Partial<ElvaFederatedRemoteModule>;
}

function isElvaRemoteModule(
  remote: Partial<ElvaFederatedRemoteModule>,
): remote is ElvaFederatedRemoteModule {
  return (
    typeof remote.ElvaFederatedChatProvider === 'function' &&
    typeof remote.ElvaChatLauncher === 'function' &&
    typeof remote.useElvaChat === 'function'
  );
}

/** Runtime federation: load chat remote from chat.elva.ai without vite federation in the host bundle. */
export async function loadElvaRemote(): Promise<ElvaFederatedRemoteModule> {
  const container = await loadRemoteContainer();
  const exposedFactory = await container.get('./ElvaFederatedChat');
  const exposedModule = normalizeRemoteExports(exposedFactory());

  if (!isElvaRemoteModule(exposedModule)) {
    throw new Error('Elva AI chat remote did not expose the expected exports');
  }

  return exposedModule;
}
